import { useState, useEffect, useCallback, useDeferredValue, useMemo, useRef } from "react";
import { Container, Row, Col, Accordion, Badge, Button, Form, Spinner } from "react-bootstrap";
import { TournamentList, type TournamentData } from "./components/Tournaments";
import { TournamentSetList } from "./components/Sets";
import { buildSetListData, type SetData } from "./util/setData";
import { SetInfo } from "./components/SetInfo";
import FocusedTournament from "./components/FocusedTournament";
import FocusedPlayer from "./components/FocusedPlayer";
import Navbar from "./components/Navbar";
import type {
  Tournament,
  TournamentSummary,
  PlayerDetailResponse,
  PlayerLookupResult,
} from "../shared/types";
import { setBracketLabel } from "../shared/setGroups";
import "./App.css";
import Footer from "./components/Footer";

type Focus = { type: "tournament"; slug: string } | { type: "player"; publicId: string } | null;

/** `/player/<publicId>` is a player focus; any other single non-empty segment is a tournament slug. */
function parseFocus(pathname: string): Focus {
  const segments = pathname.replace(/^\/+/, "").split("/").filter(Boolean);
  if (segments.length === 0) return null;
  if (segments[0] === "player" && segments[1]) return { type: "player", publicId: segments[1] };
  if (segments.length === 1) return { type: "tournament", slug: segments[0] };
  return null;
}

export default function App() {
  // Slim catalog list (fast first paint); full per-tournament data is fetched on
  // demand into `details`, keyed by folder.
  const [summaries, setSummaries] = useState<TournamentSummary[]>([]);
  const [details, setDetails] = useState<Record<string, Tournament>>({});
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [selectedTournament, setSelectedTournament] = useState<string | null>(null);
  const [selectedSetId, setSelectedSetId] = useState<string | null>(null);
  const [mountedTournaments, setMountedTournaments] = useState<string[]>([]);
  // URL-driven focused page (tournament or player). null = the catalog at "/".
  // Kept in sync with the address bar via pushState (selection) and popstate
  // (back/forward).
  const [focus, setFocus] = useState<Focus>(() => parseFocus(window.location.pathname));
  // Keyed by publicId so "loading"/"failed" for the currently focused player
  // are derived from comparing against `focus.publicId` (below) rather than
  // tracked as separate state that an effect would need to reset synchronously.
  const [playerFetch, setPlayerFetch] = useState<{
    publicId: string;
    data: PlayerDetailResponse | null;
    failed: boolean;
  } | null>(null);
  const deferredMounted = useDeferredValue(mountedTournaments);
  const [search, setSearch] = useState("");
  const [playerSearch, setPlayerSearch] = useState("");
  // Defer the value that drives filtering so the input/clear button stay
  // responsive while the expensive list recompute happens at lower priority.
  const deferredPlayerSearch = useDeferredValue(playerSearch);
  const [theme, setTheme] = useState<"light" | "dark">(() => {
    return (localStorage.getItem("theme") as "light" | "dark") ?? "light";
  });

  useEffect(() => {
    document.documentElement.setAttribute("data-bs-theme", theme);
    localStorage.setItem("theme", theme);
  }, [theme]);

  const toggleTheme = useCallback(() => {
    setTheme((prev) => (prev === "light" ? "dark" : "light"));
  }, []);

  // Extracted so the "Try again" retry button can re-run the same fetch.
  const fetchSummaries = useCallback(() => {
    fetch("/api/tournaments/summary")
      .then((res) => (res.ok ? res.json() : Promise.reject(new Error(String(res.status)))))
      .then((data: TournamentSummary[]) => setSummaries(data))
      .catch(() => setLoadError(true))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    fetchSummaries();
  }, [fetchSummaries]);

  const retryFetchSummaries = useCallback(() => {
    setLoading(true);
    setLoadError(false);
    fetchSummaries();
  }, [fetchSummaries]);

  // Keep focus in sync when the user navigates with back/forward.
  useEffect(() => {
    const onPop = () => setFocus(parseFocus(window.location.pathname));
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
  }, []);

  // Slug ⇄ folder maps come straight from the summaries (the server precomputes
  // each tournament's slug), so the catalog needs no set-level data to route.
  const { slugToFolder, folderToSlug } = useMemo(() => {
    const slugToFolder = new Map<string, string>();
    const folderToSlug = new Map<string, string>();
    for (const t of summaries) {
      if (!t.slug) continue;
      slugToFolder.set(t.slug, t.folder);
      folderToSlug.set(t.folder, t.slug);
    }
    return { slugToFolder, folderToSlug };
  }, [summaries]);

  // Fetch a tournament's full data once (sets, contexts, game summaries) and
  // cache it. In-flight folders are tracked so concurrent triggers don't double
  // fetch. Best-effort: a failure just leaves the entry unloaded.
  const fetchingRef = useRef<Set<string>>(new Set());
  const ensureDetail = useCallback(
    (folder: string) => {
      if (!folder || details[folder] || fetchingRef.current.has(folder)) return;
      const slug = folderToSlug.get(folder);
      if (!slug) return;
      fetchingRef.current.add(folder);
      fetch(`/api/tournaments/by-slug/${encodeURIComponent(slug)}`)
        .then((res) => (res.ok ? res.json() : Promise.reject(new Error(String(res.status)))))
        .then((t: Tournament) => setDetails((prev) => ({ ...prev, [folder]: t })))
        .catch(() => {})
        .finally(() => fetchingRef.current.delete(folder));
    },
    [details, folderToSlug],
  );

  const focusedTournamentSlug = focus?.type === "tournament" ? focus.slug : null;
  const focusedFolder = focusedTournamentSlug
    ? (slugToFolder.get(focusedTournamentSlug) ?? null)
    : null;
  const focusedTournament = focusedFolder ? (details[focusedFolder] ?? null) : null;

  // Pull the focused tournament's full data as soon as its slug resolves.
  useEffect(() => {
    if (focusedFolder) ensureDetail(focusedFolder);
  }, [focusedFolder, ensureDetail]);

  // Unknown tournament slug (no match once loaded): tidy the address bar back
  // to "/" - the render below already falls through to the catalog.
  useEffect(() => {
    if (
      focus?.type === "tournament" &&
      !loading &&
      !focusedFolder &&
      window.location.pathname !== "/"
    ) {
      window.history.replaceState({}, "", "/");
    }
  }, [loading, focus, focusedFolder]);

  const focusedPlayerPublicId = focus?.type === "player" ? focus.publicId : null;

  // Fetch the focused player's full cross-tournament record directly by their
  // permanent public id - unlike tournaments there's no pre-loaded catalog to
  // resolve against, and only one player can be focused at a time, so no
  // multi-item cache is needed.
  useEffect(() => {
    if (!focusedPlayerPublicId) return;
    let cancelled = false;
    const publicId = focusedPlayerPublicId;
    fetch(`/api/players/${encodeURIComponent(publicId)}`)
      .then((res) => (res.ok ? res.json() : Promise.reject(new Error(String(res.status)))))
      .then((p: PlayerDetailResponse) => {
        if (!cancelled) setPlayerFetch({ publicId, data: p, failed: false });
      })
      .catch(() => {
        if (!cancelled) setPlayerFetch({ publicId, data: null, failed: true });
      });
    return () => {
      cancelled = true;
    };
  }, [focusedPlayerPublicId]);

  const focusedPlayer =
    focus?.type === "player" && playerFetch?.publicId === focus.publicId ? playerFetch.data : null;
  const focusedPlayerLoading = focus?.type === "player" && playerFetch?.publicId !== focus.publicId;
  const focusedPlayerFailed =
    focus?.type === "player" && playerFetch?.publicId === focus.publicId && playerFetch.failed;

  // Unknown/unresolved player slug: leave the URL alone and show why, instead of silently bouncing to "/".

  const openTournamentPage = useCallback(
    (folder: string) => {
      const slug = folderToSlug.get(folder);
      if (!slug) return;
      window.history.pushState({}, "", `/${slug}`);
      setFocus({ type: "tournament", slug });
    },
    [folderToSlug],
  );

  const openPlayerPage = useCallback((publicId: string) => {
    window.history.pushState({}, "", `/player/${publicId}`);
    setFocus({ type: "player", publicId });
  }, []);

  const closeFocus = useCallback(() => {
    window.history.pushState({}, "", "/");
    setFocus(null);
  }, []);

  const handleReset = useCallback(() => {
    setSelectedTournament(null);
    setSelectedSetId(null);
    setSearch("");
    setPlayerSearch("");
    if (window.location.pathname !== "/") window.history.pushState({}, "", "/");
    setFocus(null);
  }, []);

  const playerQuery = deferredPlayerSearch.toLowerCase();

  const filtered = summaries.filter((t) => {
    const matchesName = t.folder.toLowerCase().includes(search.toLowerCase());
    const matchesPlayer =
      !playerQuery || t.players.some((p) => p.toLowerCase().includes(playerQuery));
    return matchesName && matchesPlayer;
  });

  const activeTournament = selectedTournament ? (details[selectedTournament] ?? null) : null;
  // The selected tournament is in the list but its full data hasn't arrived yet.
  const activeDetailLoading = selectedTournament !== null && !details[selectedTournament];

  const tournamentListData: TournamentData[] = filtered
    .map((t) => ({
      name: t.folder,
      location: t.location,
      setCount: t.setCount,
      entrants: t.entrants,
      date: t.latestStartMs ? new Date(t.latestStartMs).toLocaleDateString() : "",
      playlistId: t.playlistId,
      slug: t.slug,
      _startMs: t.latestStartMs,
    }))
    .sort((a, b) => b._startMs - a._startMs);

  const activeSetListData: SetData[] = useMemo(
    () => (activeTournament ? buildSetListData(activeTournament, deferredPlayerSearch) : []),
    [activeTournament, deferredPlayerSearch],
  );

  const selectedSet = activeSetListData.find((s) => s.id === selectedSetId) ?? null;
  const selectedBracket = selectedSet ? setBracketLabel(selectedSet.startgg) : null;

  // Which of the active tournament's raw entrant names already resolve to a
  // canonical player page, so the Set Info panel can make them clickable -
  // forwarded up from the active tournament's own TournamentSetList (below)
  // rather than fetched again here, since it already resolves this same
  // tournament's names for its own set list.
  const [selectedSetPlayerLookup, setSelectedSetPlayerLookup] = useState<PlayerLookupResult>({});

  const isMountingActive =
    selectedTournament !== null &&
    mountedTournaments.includes(selectedTournament) &&
    !deferredMounted.includes(selectedTournament);

  const totalSets = summaries.reduce((acc, t) => acc + t.setCount, 0);

  // Determine which mobile accordion panel should be open
  const mobilePanel = selectedSet ? "setinfo" : selectedTournament ? "sets" : "tournaments";

  const [mobileExpanded, setMobileExpanded] = useState<string>(mobilePanel);
  const [prevMobilePanel, setPrevMobilePanel] = useState(mobilePanel);
  if (prevMobilePanel !== mobilePanel) {
    setPrevMobilePanel(mobilePanel);
    setMobileExpanded(mobilePanel);
  }

  const handleSelectSet = useCallback((id: string) => {
    setSelectedSetId((prev) => (prev === id ? null : id));
  }, []);

  const tournamentContent = (
    <TournamentList
      tournaments={tournamentListData}
      selectedTournament={selectedTournament}
      onSelectTournament={(name) => {
        setSelectedTournament((prev) => (prev === name ? null : name));
        setSelectedSetId(null);
        setMountedTournaments((prev) => (prev.includes(name) ? prev : [...prev, name]));
        ensureDetail(name);
      }}
      onOpenPage={openTournamentPage}
    />
  );

  const setsContent = (
    <>
      {deferredMounted.map((name) => {
        const t = details[name];
        if (!t) return null;
        const isActive = name === selectedTournament;
        return (
          <TournamentSetList
            key={name}
            tournament={t}
            playerQuery={deferredPlayerSearch}
            selectedSetId={isActive ? selectedSetId : null}
            onSelectSet={handleSelectSet}
            onOpenPlayer={openPlayerPage}
            visible={isActive && !isMountingActive}
            onPlayerLookup={isActive ? setSelectedSetPlayerLookup : undefined}
          />
        );
      })}
    </>
  );

  const tournamentsCard = (
    <div className="h-100 bg-body-secondary rounded d-flex flex-column overflow-hidden pb-3">
      <h5 className="px-3 py-2 mb-0 border-bottom text-center">
        Tournaments
        {playerSearch && (
          <Badge bg="primary" className="ms-2 fw-normal" style={{ fontSize: "0.65em" }}>
            <i className="bi bi-funnel-fill me-1" />
            {playerSearch}
          </Badge>
        )}
      </h5>
      <div className="px-3 pt-2">
        <Form.Control
          size="sm"
          type="text"
          placeholder="Search tournaments..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </div>
      <div className="flex-grow-1 overflow-auto px-3 pt-3">
        {loading ? (
          <div className="text-center py-4">
            <Spinner size="sm" /> Loading tournaments...
          </div>
        ) : loadError ? (
          <div className="text-center py-4">
            <p className="text-muted mb-2">Couldn't load tournaments.</p>
            <Button size="sm" variant="outline-secondary" onClick={retryFetchSummaries}>
              Try again
            </Button>
          </div>
        ) : tournamentListData.length === 0 ? (
          <p className="text-muted py-3 text-center">No tournaments found</p>
        ) : (
          tournamentContent
        )}
      </div>
    </div>
  );

  const setsCard = (
    <div className="h-100 bg-body-secondary rounded d-flex flex-column overflow-hidden pb-3">
      <h5 className="px-3 py-2 mb-0 border-bottom text-center">
        Sets
        {playerSearch && (
          <Badge bg="primary" className="ms-2 fw-normal" style={{ fontSize: "0.65em" }}>
            <i className="bi bi-funnel-fill me-1" />
            {playerSearch}
          </Badge>
        )}
      </h5>
      <div className="flex-grow-1 overflow-auto px-3 pt-3">
        {!selectedTournament && (
          <p className="text-muted py-3 text-center">Select a tournament to view sets</p>
        )}
        {(isMountingActive || activeDetailLoading) && (
          <div className="text-center py-4">
            <Spinner size="sm" /> Loading sets...
          </div>
        )}
        {setsContent}
      </div>
    </div>
  );

  const setInfoCard = (
    <div className="h-100 bg-body-secondary rounded d-flex flex-column overflow-hidden pb-3">
      <h5 className="px-3 py-2 mb-0 border-bottom text-center">Set Info</h5>
      <div className="flex-grow-1 overflow-auto px-3 pt-3">
        {selectedSet ? (
          <SetInfo
            key={selectedSet.id}
            set={selectedSet}
            playerLookup={selectedSetPlayerLookup}
            onOpenPlayer={openPlayerPage}
          />
        ) : (
          <p className="text-muted py-3 text-center">Select a set to view details</p>
        )}
      </div>
    </div>
  );

  return (
    <div className="d-flex flex-column vh-100">
      <Navbar
        theme={theme}
        onToggleTheme={toggleTheme}
        onReset={handleReset}
        playerSearch={playerSearch}
        onPlayerSearchChange={setPlayerSearch}
      />

      {focus?.type === "tournament" && focusedTournament ? (
        <FocusedTournament
          tournament={focusedTournament}
          onBack={closeFocus}
          onOpenPlayer={openPlayerPage}
        />
      ) : focus?.type === "tournament" && (loading || focusedFolder) ? (
        // Either the catalog is still loading (slug not yet resolvable) or the
        // slug resolved and we're fetching that tournament's full data.
        <main className="flex-grow-1 d-flex align-items-center justify-content-center text-muted">
          <Spinner size="sm" /> <span className="ms-2">Loading…</span>
        </main>
      ) : focus?.type === "player" && focusedPlayer ? (
        <FocusedPlayer
          player={focusedPlayer}
          onBack={closeFocus}
          onOpenTournament={openTournamentPage}
          onOpenPlayer={openPlayerPage}
        />
      ) : focus?.type === "player" && focusedPlayerLoading ? (
        <main className="flex-grow-1 d-flex align-items-center justify-content-center text-muted">
          <Spinner size="sm" /> <span className="ms-2">Loading…</span>
        </main>
      ) : focus?.type === "player" && focusedPlayerFailed ? (
        <main className="flex-grow-1 d-flex flex-column align-items-center justify-content-center text-muted gap-3">
          <span>This player couldn&rsquo;t be found - the link may be out of date.</span>
          <Button variant="outline-secondary" size="sm" onClick={closeFocus}>
            Back to catalog
          </Button>
        </main>
      ) : (
        <>
          {/* Desktop layout (lg–xl): Tournaments stacked over Sets on the left, Set Info on the right */}
          <Container
            fluid
            as="main"
            className="flex-grow-1 overflow-hidden p-3 d-none d-lg-block d-xxl-none"
          >
            <Row className="h-100 g-3">
              <Col lg={6} className="h-100 overflow-hidden">
                <div className="d-flex flex-column h-100 gap-3">
                  <div className="overflow-hidden" style={{ flex: "1 1 0", minHeight: 0 }}>
                    {tournamentsCard}
                  </div>
                  <div className="overflow-hidden" style={{ flex: "1 1 0", minHeight: 0 }}>
                    {setsCard}
                  </div>
                </div>
              </Col>

              <Col lg={6} className="h-100 overflow-hidden">
                {setInfoCard}
              </Col>
            </Row>
          </Container>

          {/* Desktop layout (xxl+): 3-column grid */}
          <Container fluid as="main" className="flex-grow-1 overflow-hidden p-3 d-none d-xxl-block">
            <Row className="h-100 g-3">
              <Col xxl={3} className="h-100 overflow-hidden">
                {tournamentsCard}
              </Col>
              <Col xxl={3} className="h-100 overflow-hidden">
                {setsCard}
              </Col>
              <Col xxl={6} className="h-100 overflow-hidden">
                {setInfoCard}
              </Col>
            </Row>
          </Container>

          {/* Mobile layout: collapsible accordion panels (below lg) */}
          <main className="flex-grow-1 overflow-auto p-2 d-lg-none">
            <Accordion
              activeKey={mobileExpanded}
              onSelect={(key) => setMobileExpanded((key as string) ?? "")}
              id="mobile-panels"
            >
              {/* Tournaments panel */}
              <Accordion.Item eventKey="tournaments">
                <Accordion.Header>
                  Tournaments
                  <Badge bg="secondary" className="ms-2">
                    {tournamentListData.length}
                  </Badge>
                  {playerSearch && (
                    <Badge bg="primary" className="ms-1 fw-normal" style={{ fontSize: "0.7em" }}>
                      <i className="bi bi-funnel-fill me-1" />
                      {playerSearch}
                    </Badge>
                  )}
                </Accordion.Header>
                <Accordion.Body className="p-2">
                  <Form.Control
                    size="sm"
                    className="mb-2"
                    type="text"
                    placeholder="Search tournaments..."
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                  />
                  {loading ? (
                    <div className="text-center py-4">
                      <Spinner size="sm" /> Loading tournaments...
                    </div>
                  ) : loadError ? (
                    <div className="text-center py-4">
                      <p className="text-muted mb-2">Couldn't load tournaments.</p>
                      <Button size="sm" variant="outline-secondary" onClick={retryFetchSummaries}>
                        Try again
                      </Button>
                    </div>
                  ) : tournamentListData.length === 0 ? (
                    <p className="text-muted py-3 text-center">No tournaments found</p>
                  ) : (
                    tournamentContent
                  )}
                </Accordion.Body>
              </Accordion.Item>

              {/* Sets panel */}
              <Accordion.Item eventKey="sets">
                <Accordion.Header>
                  Sets{selectedTournament && ` - ${selectedTournament}`}
                  {selectedTournament && !isMountingActive && !activeDetailLoading && (
                    <Badge bg="secondary" className="ms-2">
                      {activeSetListData.length}
                    </Badge>
                  )}
                  {playerSearch && (
                    <Badge bg="primary" className="ms-1 fw-normal" style={{ fontSize: "0.7em" }}>
                      <i className="bi bi-funnel-fill me-1" />
                      {playerSearch}
                    </Badge>
                  )}
                </Accordion.Header>
                <Accordion.Body className="p-2">
                  {!selectedTournament && (
                    <p className="text-muted py-3 text-center">Select a tournament to view sets</p>
                  )}
                  {(isMountingActive || activeDetailLoading) && (
                    <div className="text-center py-4">
                      <Spinner size="sm" /> Loading sets...
                    </div>
                  )}
                  {setsContent}
                </Accordion.Body>
              </Accordion.Item>

              {/* Set Info panel */}
              <Accordion.Item eventKey="setinfo">
                <Accordion.Header>
                  Set Info
                  {selectedSet && (
                    <>
                      {` - ${selectedSet.startgg.set.fullRoundText}`}
                      {selectedBracket && (
                        <small className="text-muted ms-2">{selectedBracket}</small>
                      )}
                    </>
                  )}
                </Accordion.Header>
                <Accordion.Body className="p-2">
                  {selectedSet ? (
                    <>
                      <Button
                        variant="outline-secondary"
                        size="sm"
                        className="mb-2"
                        onClick={() => setMobileExpanded("sets")}
                      >
                        <i className="bi bi-arrow-left me-1" />
                        Back to Sets
                      </Button>
                      <SetInfo
                        key={selectedSet.id}
                        set={selectedSet}
                        playerLookup={selectedSetPlayerLookup}
                        onOpenPlayer={openPlayerPage}
                      />
                    </>
                  ) : (
                    <p className="text-muted py-3 text-center">Select a set to view details</p>
                  )}
                </Accordion.Body>
              </Accordion.Item>
            </Accordion>
          </main>
        </>
      )}

      <Footer tournamentCount={summaries.length} setCount={totalSets} />
    </div>
  );
}
